import { type AxiosError } from 'axios'
import moment from 'moment'

import { getTimezoneHoursOffset } from 'components/common/schedule-window-utils'
import toast from 'lib/toast'
import { ScheduleDay, ScheduleDayLookup } from 'types/schedules/schedules'
import { type MomentParsableDate } from 'types/time'
import { messageFromResponseError } from 'utils/strings'

export function handleServiceFormSubmitError(error: AxiosError) {
  const displayText = messageFromResponseError(
    error,
    'There was an issue saving your changes. Please try again later.'
  )

  toast({ type: 'error', text: displayText })
}

/**
 * Returns a numeric integer representing the day offset given a start time and the
 * differential in hours as a result of the timezone change, or zero if no offset is needed.
 */
export function getScheduleDayOffset(
  startTime: MomentParsableDate,
  timezone: string,
  hoursDelta: number
) {
  const momentStart = moment(startTime).tz(timezone)
  // If we shifted backwards past midnight, move active days backward
  const shiftBackwards = momentStart.hour() - hoursDelta >= 24
  // If we shifted forwards past midnight, move active days forward
  const shiftForwards = momentStart.hour() - hoursDelta < 0

  if (shiftBackwards) return -1
  if (shiftForwards) return 1
  return 0
}

/**
 * Maps the delta offset of days to a new ScheduleDay, wrapping the week as necessary.
 */
export function shiftScheduleDayByOffset(day: ScheduleDay, daysDelta: number) {
  const momentDayKey = ScheduleDayLookup[day]
  const momentDay = moment().day(momentDayKey).add(daysDelta, 'days')

  return ScheduleDay[momentDay.format('dddd')]
}

/**
 * Convenience function which combines the logic of detecting the hours offset (relative
 * to UTC) and then calculating a numeric coefficient (bound by -1 to 1) representing the
 * directional shift to be applied to the set of active days.
 *
 * @example
 * // For a start time of 23:00 and a timezone UTC+3, this would return 1
 * getActiveDaysOffset('2024-01-01T23:00:00Z', 'Europe/Moscow')
 *
 * // For a start time of 01:00 and a timezone UTC-5, this would return -1
 * getActiveDaysOffset('2024-01-01T01:00:00Z', 'America/New_York')
 *
 * // For a start time of 12:00 and a timezone UTC+2, this would return 0
 * getActiveDaysOffset('2024-01-01T12:00:00Z', 'Europe/Berlin')
 */
export function getActiveDaysOffset(
  startTime: MomentParsableDate,
  timezone: string,
  targetTimezone = 'UTC'
) {
  const hoursDelta = getTimezoneHoursOffset(timezone, targetTimezone)
  return getScheduleDayOffset(startTime, timezone, hoursDelta)
}
